import type { RetrievalOutcome } from './experience';

export type RecoveryDecision =
  | { kind: 'retry-same'; reason: string }
  | { kind: 'replan-conditions'; reason: string }
  | { kind: 'replan-target'; reason: string }
  // 같은 조건으로 계속 비었다 — 조건을 더 만져도 안 나온다. 화면을 다시 고를 자리다.
  | { kind: 'replan-screen'; reason: string }
  | { kind: 'stop'; reason: string };

/** 실패 문자열이 아니라 검증기의 typed outcome으로 다음 행동을 고른다. */
export function decideRecovery(outcomes: RetrievalOutcome[], round: number): RecoveryDecision {
  if (outcomes.some((outcome) => outcome.kind === 'permission-denied')) {
    return { kind: 'stop', reason: '이 업무를 조회할 화면 권한이 없다' };
  }
  if (outcomes.some((outcome) => outcome.kind === 'condition-rejected')) {
    return { kind: 'replan-conditions', reason: '화면이 검색조건을 거부해 선택값을 다시 맞춰야 한다' };
  }
  // **화면이 서버 응답을 못 받은 것은 자료 사정이 아니다.** 사람은 조회를 한 번 더 누른다. 다른 조회가 됐어도
  // 그렇다. 실제 테넌트(2026-10-26)에서 10월만 응답이 없었는데 아래 「모두 비었을 때만」에 걸려 다시 조회하지
  // 않았다. 한 번 더 해도 없으면 멈춘다. 화면을 바꿔 다시 계획해도 서버는 그대로다.
  const silent = outcomes.find((outcome) => outcome.kind === 'no-answer');
  if (silent) {
    return round === 0
      ? { kind: 'retry-same', reason: '화면이 서버 응답을 못 받아 같은 조회를 한 번 더 한다' }
      : { kind: 'stop', reason: `${silent.label} 조회에 화면이 서버 응답을 못 받았다` };
  }
  if (outcomes.every((outcome) => outcome.kind === 'legitimate-empty') && round === 0) {
    return { kind: 'retry-same', reason: '렌더링 지연일 수 있어 같은 조회를 한 번 더 확인한다' };
  }
  if (outcomes.some((outcome) => ['wrong-grid', 'missing-column', 'empty-column', 'route-mismatch']
    .includes(outcome.kind))) {
    return { kind: 'replan-target', reason: '결과 표나 화면의 데이터 계약을 다시 골라야 한다' };
  }
  // **여기서 「다른 화면은 마지막 수단」이라고 말하면 안 된다.** 빈 표는 데이터 계약을
  // 충족한다(표는 있고 행만 없다) — 그래서 `replan-target` 의 쪽지("충족할 수 없을 때만
  // 다른 화면")대로면 모델은 같은 화면을 지킨다. 실제로 점검계획이 세 판을 돌면서 같은
  // 화면을 다시 골랐다(실측 2026-09-14). 결정의 이유와 쪽지가 서로 반대로 말하고 있었다.
  return { kind: 'replan-screen', reason: '같은 조건의 결과가 계속 비어 다른 화면을 찾아야 한다' };
}

export function recoveryNote(decision: RecoveryDecision): string {
  if (decision.kind === 'replan-conditions') {
    return ' 화면과 결과 표는 유지할 수 있지만 실제 선택값·필수 조건을 먼저 교정하라.';
  }
  if (decision.kind === 'replan-target') {
    return ' 현재 데이터 계약으로 충족할 수 없을 때만 다른 결과 표나 화면을 골라라.';
  }
  if (decision.kind === 'replan-screen') {
    // 조건을 또 만지라고 하면 같은 0 이 한 번 더 나온다. 화면을 바꾸라고 곧장 말한다.
    return ' 조건을 더 만지지 말고 **다른 화면**을 골라라. 같은 화면을 또 고르면 결과도 같다.'
      + ' 목록에서 질문이 가리키는 업무에 이름이 더 맞는 화면을 찾아라.';
  }
  return '';
}

export class RecoveryStoppedError extends Error {
  constructor(readonly reason: string) {
    super(`조회 복구 중단: ${reason}`);
    this.name = 'RecoveryStoppedError';
  }
}
